import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { apiUrl } from "../../../config/config.js";
import { useHandleActionItem } from "../../../hooks/useHandleActionItem.js";
import InputDate from "../../../components/forms/InputDate.jsx";

const Toast = Swal.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	timer: 3000,
	timerProgressBar: true,
	didOpen: (toast) => {
		toast.onmouseenter = Swal.stopTimer;
		toast.onmouseleave = Swal.resumeTimer;
	},
});

const excludeKeys = [
	"password",
	"cardDetails",
	"realToken",
	"demoToken",
	"image",
	"profileImage",
	"actieCustomer",
	"activeDrawings",
	"activeCustomer",
	"description",
	"buyTo",
	"bankResponse",
	"invoiceId",
];

// Diccionario para traducir claves
const keyTranslation = {
	ID: "id",
	"ID Usuario": "userId",
	Monto: "amount",
	Estado: "status",
	Creado: "createdAt",
	Expira: "expireAt",
};

// Status options for dropdown
const statusOptions = [
	{ value: "paid", label: "Pagado" },
	{ value: "pending", label: "Pendiente" },
];

// Función para formatear fechas al formato YYYY-MM-DD
const formatDateForInput = (dateString) => {
	if (!dateString) return "";

	// Si ya está en formato ISO, extraemos solo la parte de la fecha
	if (dateString.includes("T")) {
		return dateString.split("T")[0];
	}

	// Si es una fecha en otro formato, intentamos convertirla
	const date = new Date(dateString);
	if (isNaN(date.getTime())) return ""; // Fecha inválida

	// Formatear a YYYY-MM-DD
	return date.toISOString().split("T")[0];
};

function AppIbPurchasesDetails() {
	const location = useLocation();
	const navigate = useNavigate();
	const locationType = location.state?.type || "";
	const rawData = location.state?.data || {};
	const { handleDelete, handleSubmit } = useHandleActionItem();

	const generateEmptyFields = () => {
		return {
			userId: "",
			amount: "",
			status: "",
			createdAt: "",
			expireAt: "",
		};
	};

	const translateKeys = (data) => {
		const result = {};
		Object.entries(data).forEach(([key, value]) => {
			const translatedKey = keyTranslation[key] || key;
			if (!excludeKeys.includes(translatedKey)) {
				// Formatear fechas si es necesario
				if (translatedKey === "createdAt" || translatedKey === "expireAt") {
					result[translatedKey] = formatDateForInput(value);
				} else {
					result[translatedKey] = value;
				}
			}
		});
		return result;
	};

	const [formValues, setFormValues] = useState(locationType === "add" ? generateEmptyFields() : translateKeys(rawData));

	useEffect(() => {
		if (locationType === "edit") {
			setFormValues(translateKeys(rawData));
		}
	}, [rawData, locationType]);

	const validateForm = () => {
		for (const key in formValues) {
			if (!formValues[key] && formValues[key] !== 0) {
				Toast.fire({
					icon: "error",
					title: `El campo ${key} es requerido`,
					background: "#1e2836",
				});
				return false;
			}
		}
		return true;
	};

	const handleInputChange = (e, key) => {
		const value = e.target.value;
		setFormValues((prev) => ({ ...prev, [key]: value }));
	};

	const handleSubmitForm = async (event) => {
		event.preventDefault();
		if (!validateForm()) return;
		await handleSubmit(locationType === "add" ? "add" : "update", "purchases", {
			...formValues,
			id: formValues.id || rawData.ID,
		});
		navigate("/purchases");
	};

	const handleDeleteForm = async (event) => {
		event.preventDefault();
		await handleDelete({ id: formValues.id || rawData.ID }, "purchases", true);
	};

	return (
		<div>
			<form onSubmit={handleSubmitForm}>
				<h1 className="text-center">{locationType === "add" ? "Agregar compra" : "Actualizar compra"}</h1>

				{Object.entries(formValues).map(([key, value]) => {
					// Para los campos de fecha
					if (key === "createdAt" || key === "expireAt") {
						return (
							<div className="mb-3" key={key}>
								<label className="form-label">
									{key.charAt(0).toUpperCase() + key.slice(1)} <span className="text-danger">*</span>
								</label>
								<input
									type="date"
									className="form-control bg-white bg-opacity-5"
									value={value || ""}
									onChange={(e) => handleInputChange(e, key)}
								/>
							</div>
						);
					}
					// Para el campo status
					else if (key === "status") {
						return (
							<div className="mb-3" key={key}>
								<label className="form-label">
									{key.charAt(0).toUpperCase() + key.slice(1)} <span className="text-danger">*</span>
								</label>
								<select className="form-select bg-white bg-opacity-5" value={value || ""} onChange={(e) => handleInputChange(e, key)}>
									<option value="">Seleccionar estado</option>
									{statusOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						);
					}
					// Para todos los demás campos
					else {
						return (
							<div className="mb-3" key={key}>
								<label className="form-label">
									{key.charAt(0).toUpperCase() + key.slice(1)} <span className="text-danger">*</span>
								</label>
								<input
									className="form-control bg-white bg-opacity-5"
									type="text"
									value={value || ""}
									onChange={(e) => handleInputChange(e, key)}
								/>
							</div>
						);
					}
				})}

				<div className="mb-3 d-flex justify-content-around">
					<button type="submit" className="btn btn-outline-theme btn-lg">
						{locationType === "add" ? "Agregar" : "Actualizar"}
					</button>
					<button onClick={() => navigate("/purchases")} className="btn btn-outline-theme btn-lg">
						Atrás
					</button>
					<button onClick={handleDeleteForm} className="btn btn-outline-danger btn-lg">
						Eliminar
					</button>
				</div>
			</form>
		</div>
	);
}

export default AppIbPurchasesDetails;
